// order-domain: ownership=kkren copyPolicy=copy-and-prune-mgb pruneAfter=brand-retirement
// 混合拆单第二单（例外件海快单）的金额预估：不另写一套费用公式，
// 而是把例外件的 prepare 模型放进一个临时 store，用下单页同一套 feeSummary 引擎、
// 同样的默认值（税制恢复规则、个人用途、默认派件物流、第一单收件地址）算出合计与费用行。
import Vue from 'vue'
import Vuex from 'vuex'
import jyorderCreateModule from '@g/store/modules/jyo/jyorderCreate'
import { createInitialModuleState } from '@g/store/modules/jyo/jyorderCreate/state'
import { CREATE_ORDER_USE_FEE_TYPE_STORAGE_KEY } from '@g/order-shared/storage/constants'
import { isH5Project } from '@g/utils/projectUtils'
import { resolveFeeRestoreType } from '../prepare/initCreateContext'
import { pickSecondOrderSeRate } from './mixedSplit'

Vue.use(Vuex)

const ESTIMATE_NS = 'estimate'

// 预估费用行的短标签（对话框副文案用），税金行按税制显示全包稅／包頻繁稅
const ROW_SHORT_LABELS = Object.freeze({
  'parcels-fee': '運費',
  'sea-delivery-fee': '派件費',
  'local-delivery-fee': '派送費',
  'busy-season-fee': '繁盛期附加費',
  'overload-fee': '一般倉費用',
  'hct-pallet-fee': '棧板費',
  'hct-volume-fee': '材積費',
  'island-fee': '離島費',
  'island-discount': '離島費優惠',
  'remote-home-fee': '偏遠派送費',
  'van-fee': '專車派件費',
  'address-discount': '地址優惠',
  'temporary-discount': '限時優惠',
  'coupon-discount': '優惠券',
})

function taxRowLabel (feeType) {
  if (feeType === 'tax') return '全包稅'
  if (feeType === 'frt') return '包頻繁稅'
  return '稅金'
}

function readStorageFeeType () {
  try {
    return localStorage.getItem(CREATE_ORDER_USE_FEE_TYPE_STORAGE_KEY) || ''
  } catch (error) {
    return ''
  }
}

// 税制默认值与下单页初始化对齐：
// - 全包稅／包頻繁稅要有对应公式且通过 store 的同步可用性规则（canUseTax／canUseFrt，
//   申报金额预算需接口回填，预估时跳过，最终以第二单页面为准）
// - PC 会按 createOrderUseFeeType 恢复上次选择（第一单页面每次切换税制都会写入，
//   所以第二单打开时恢复的是第一单当下的税制，预估优先用它）；H5 初始化时线路尚未选定，不恢复
// - 都不行时退回包頻繁稅，再退回不包稅（不包稅／商業件的合计相同，稅金另计）
export function resolveEstimateFeeType ({
  rate,
  isAllIncTax,
  onlyBusi,
  onlyNotIncTax,
  storageFeeType,
  restoreFromStorage = true,
  canUseTax = Boolean(rate?.parcelsFeeFormulasInclTax),
  canUseFrt = Boolean(rate?.parcelsFeeFormulasFrt),
}) {
  if (onlyBusi) return 'busi'
  if (onlyNotIncTax) return 'notIncTax'
  const available = feeType => {
    if (feeType === 'tax') return canUseTax && Boolean(rate?.parcelsFeeFormulasInclTax)
    if (feeType === 'frt') return canUseFrt && Boolean(rate?.parcelsFeeFormulasFrt)
    return true
  }
  const restored = resolveFeeRestoreType({
    isAllIncTax,
    storageFeeType: restoreFromStorage || isAllIncTax ? storageFeeType : '',
    feeRestorePolicy: () => true,
    transport: 'se',
  })
  const candidates = [restored, 'frt', 'notIncTax'].filter(Boolean)
  return candidates.find(available) || 'notIncTax'
}

function createEstimateStore ({ rootState, rootGetters }) {
  return new Vuex.Store({
    strict: false,
    state: {
      project: rootState.project,
      edition: rootState.edition,
      userData: rootState.userData || {},
      parcel: { seaAddrids: rootState.parcel?.seaAddrids || [] },
      exchangeRate: rootState.exchangeRate,
      axios: null,
    },
    getters: {
      isLong91: () => Boolean(rootGetters?.isLong91),
    },
    modules: {
      fee: {
        namespaced: true,
        modules: {
          busySeason: {
            namespaced: true,
            getters: {
              isBusySeason: () => Boolean(rootGetters?.['fee/busySeason/isBusySeason']),
            },
          },
        },
      },
      [ESTIMATE_NS]: {
        ...jyorderCreateModule,
        state: createInitialModuleState(),
      },
    },
  })
}

// 回传 { total, rows, feeType, taxNote, rate }；没有可用海快线路时回传 null。
export function estimateSecondOrder ({
  model,
  addrInfo = null,
  tradevanAddr = null,
  rootState = {},
  rootGetters = {},
  mainUseFeeType = '',
  storageFeeType = readStorageFeeType(),
} = {}) {
  const rate = pickSecondOrderSeRate(model?.shippingRates)
  if (!rate || !model?.parcels?.length || !model.parcelsInfo) return null

  const store = createEstimateStore({ rootState, rootGetters })
  const moduleState = store.state[ESTIMATE_NS]
  store.commit(`${ESTIMATE_NS}/initCreateForm`, { pclids: model.parcels.map(parcel => parcel.pclid).join(',') })
  store.commit(`${ESTIMATE_NS}/setCreatePrepare`, model)
  store.commit(`${ESTIMATE_NS}/setSrid`, rate.srid)
  // 地址／申報人直接写入临时 state，避免 setCurrAddrInfo 触发「最近地址」本地存储副作用。
  moduleState.currAddrInfo = addrInfo
  moduleState.currTradevanAddr = tradevanAddr
  moduleState.createForm.cneeAddrid = addrInfo?.addrid || ''
  moduleState.createForm.tradevanAddrid = tradevanAddr?.addrid || ''

  const getter = name => store.getters[`${ESTIMATE_NS}/${name}`]
  const onlyBusi = Boolean(getter('onlyBusi'))
  const onlyNotIncTax = Boolean(getter('onlyNotIncTax'))
  const tpItemsInclTaxMaxWt = getter('tpItemsInclTaxMaxWt') || []
  const feeType = resolveEstimateFeeType({
    rate,
    isAllIncTax: Boolean(getter('isAllIncTax')),
    onlyBusi,
    onlyNotIncTax,
    // 第一单页面每次切换税制都会写入 storage，第二单打开时恢复的就是第一单当下的税制。
    storageFeeType: mainUseFeeType || storageFeeType,
    restoreFromStorage: !isH5Project(rootState.project),
    canUseTax: Boolean(getter('canUseTaxByOptions')?.({
      tpItemsInclTaxMaxWt,
      trfTpItems: getter('trfTpItems') || [],
      checkFeeBudget: false,
    })),
    canUseFrt: Boolean(getter('canUseFrtByOptions')?.({
      tpItemsInclTaxMaxWt,
      onlyBusi,
      onlyNotIncTax,
      isCompanyAddr: Boolean(getter('isCompanyAddr')),
      supportsFrtTax: true,
      checkFeeBudget: false,
    })),
  })
  moduleState.createForm.useFeeType = feeType

  const summary = getter('feeSummary')
  if (!summary) return null
  const rows = (summary.feeBreakdown || [])
    .filter(row => row.visible && row.id !== 'total' && row.amount > 0)
    .map(row => ({
      id: row.id,
      sign: row.sign,
      amount: row.amount,
      label: row.id === 'tax-fee' ? taxRowLabel(feeType) : (ROW_SHORT_LABELS[row.id] || row.label),
    }))
  return {
    rate,
    feeType,
    // 不包稅／商業件的合计不含稅金，文案要点明，避免与页面「稅金另計」口径混淆。
    taxNote: feeType === 'notIncTax' || feeType === 'busi' ? '不包稅' : '',
    total: Number(summary.sharedAmount) || 0,
    rows,
  }
}

export function formatEstimateRows (rows = [], taxNote = '') {
  const text = rows
    .map(row => `${row.label} ${Math.round(row.amount).toLocaleString('en-US')}${row.sign === '-' ? '（減）' : ''}`)
    .join('、')
  return text && taxNote ? `${text}（${taxNote}）` : text
}
